import type { FeatureModule, FeatureState } from '../types';

/**
 * Animation Stop – V4 (Clean 3-Layer Approach)
 *
 * Layer 1: CSS injection — kill all CSS animations/transitions via 0.001ms duration
 * Layer 2: Web Animations API — finish() all running WAAPI animations (Framer Motion etc.)
 * Layer 3: JS library detection — GSAP globalTimeline, inline-style freeze for rAF-based libs
 * Layer 4: Media — pause videos, freeze GIFs, stop SVG SMIL, stop marquees
 *
 * + MutationObserver for dynamic content
 * + Periodic re-scan (every 500ms) for frameworks that create new animations
 *
 * DOES NOT:
 *  - Monkey-patch IntersectionObserver (breaks lazy loading)
 *  - Override requestAnimationFrame (breaks entire page)
 *  - Override Element.prototype.animate() (too invasive)
 *  - Override matchMedia (too invasive)
 *  - Set opacity:1 !important via CSS (breaks modals/hidden elements)
 *  - Use animation:none or transition:none (breaks JS events)
 */
export default function createAnimationStopModule(): FeatureModule {
  let enabled = false;
  // Naming convention: every widget-injected style sits under the
  // `accessify-*` prefix so audit tools and host-side selectors that look
  // for `style[id^="accessify-"]` find every one of them. Pre-rebrand we
  // shipped `a11y-stop-animations` here, which R6-5 flagged as the only
  // outlier.
  const STYLE_ID = 'accessify-animation-stop';
  const STORAGE_KEY = 'accessify-animation-stop';

  // --- State ---
  let mutationObserver: MutationObserver | null = null;
  let scanInterval: ReturnType<typeof setInterval> | null = null;
  let scrollHandler: (() => void) | null = null;
  let originalVideoPlay: typeof HTMLVideoElement.prototype.play | null = null;
  let originalAnimate: typeof Element.prototype.animate | null = null;
  let pausedVideos: HTMLVideoElement[] = [];
  let gifOriginals = new Map<HTMLImageElement, string>();

  // =========================================================================
  // Layer 0: Element.prototype.animate() intercept
  //
  // This is THE critical piece for Framer Motion. Framer uses WAAPI
  // (Element.prototype.animate()) for ALL visual animations — hover,
  // appear, scroll, transitions. CSS rules do NOT affect WAAPI.
  // We intercept at creation time and force duration to 0.001ms
  // so every animation completes instantly.
  // =========================================================================

  /**
   * Detect animations whose keyframes only modify transform-like or filter
   * properties. These are the signature of interaction animations (hover
   * zoom, click scale, focus rings) that Framer Motion and similar libs
   * create on-the-fly. We must CANCEL these instead of FINISHING them,
   * otherwise the element lands in the visible end-state (e.g. scale(1.05)).
   */
  const VISUAL_ONLY_PROPS = new Set([
    'transform', 'scale', 'rotate', 'translate',
    'filter', 'backdropFilter', 'backdrop-filter',
    'webkitTransform', 'webkitFilter', '-webkit-transform', '-webkit-filter',
    'offset', 'easing', 'composite',
  ]);
  function isVisualOnlyKeyframes(
    keyframes: Keyframe[] | PropertyIndexedKeyframes | null,
  ): boolean {
    if (!keyframes) return false;
    try {
      if (Array.isArray(keyframes)) {
        for (const kf of keyframes) {
          if (!kf || typeof kf !== 'object') return false;
          for (const key of Object.keys(kf)) {
            if (!VISUAL_ONLY_PROPS.has(key)) return false;
          }
        }
        return true;
      }
      // PropertyIndexedKeyframes
      for (const key of Object.keys(keyframes)) {
        if (!VISUAL_ONLY_PROPS.has(key)) return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  function patchAnimate() {
    if (originalAnimate) return;
    originalAnimate = Element.prototype.animate;

    Element.prototype.animate = function (
      this: Element,
      keyframes: Keyframe[] | PropertyIndexedKeyframes | null,
      options?: number | KeyframeAnimationOptions,
    ): Animation {
      if (!enabled) {
        return originalAnimate!.call(this, keyframes, options);
      }

      // Never interfere with the widget's own animations. The widget lives
      // in shadow DOM under #accessify-root — walk up through shadow roots
      // and skip if we find the accessify host.
      try {
        let node: Node | null = this;
        while (node) {
          if ((node as Element).id === 'accessify-root') {
            return originalAnimate!.call(this, keyframes, options);
          }
          const parent: Node | null = (node as any).parentNode || (node as any).host || null;
          if (parent === node) break;
          node = parent;
        }
      } catch { /* ignore */ }

      const visualOnly = isVisualOnlyKeyframes(keyframes);

      // Force instant completion: duration 0.001ms, no delay, 1 iteration, fill forwards
      let opts: KeyframeAnimationOptions;
      if (typeof options === 'number') {
        opts = { duration: 0.001, fill: 'forwards' as FillMode };
      } else {
        opts = {
          ...(options || {}),
          duration: 0.001,
          delay: 0,
          iterations: 1,
          fill: 'forwards' as FillMode,
        };
        // Remove iterationStart, endDelay etc. that could interfere
        delete (opts as any).iterationStart;
        delete (opts as any).endDelay;
      }

      const anim = originalAnimate!.call(this, keyframes, opts);
      try {
        if (visualOnly) {
          anim.cancel();
        } else {
          anim.finish();
        }
      } catch { /* ignore */ }
      return anim;
    };
  }

  function restoreAnimate() {
    if (originalAnimate) {
      Element.prototype.animate = originalAnimate;
      originalAnimate = null;
    }
  }

  // =========================================================================
  // Layer 1: CSS Injection
  // =========================================================================

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      *, *::before, *::after {
        animation-duration: 0.001ms !important;
        animation-iteration-count: 1 !important;
        animation-delay: 0s !important;
        animation-fill-mode: forwards !important;
        transition-duration: 0.001ms !important;
        transition-delay: 0s !important;
        scroll-behavior: auto !important;
        animation-timeline: auto !important;
        scroll-timeline: none !important;
      }
      /* Neutralize interaction transforms (hover zoom, click scale, focus
         lift). Without this, CSS like \`.card:hover { transform: scale(1.05) }\`
         still visibly changes the element — just instantly. Users who
         disable animations expect NO motion including the end-state. */
      *:not(#accessify-root):not(#accessify-root *):hover,
      *:not(#accessify-root):not(#accessify-root *):focus,
      *:not(#accessify-root):not(#accessify-root *):focus-within,
      *:not(#accessify-root):not(#accessify-root *):focus-visible,
      *:not(#accessify-root):not(#accessify-root *):active {
        transform: none !important;
        scale: none !important;
        rotate: none !important;
        translate: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  function removeStyles() {
    document.getElementById(STYLE_ID)?.remove();
  }

  // =========================================================================
  // Layer 2: Web Animations API — finish all WAAPI animations
  // =========================================================================

  function isWidgetAnimation(anim: Animation): boolean {
    try {
      const target = (anim.effect as any)?.target as Element | null;
      if (!target) return false;
      let node: Node | null = target;
      while (node) {
        if ((node as Element).id === 'accessify-root') return true;
        const parent: Node | null = (node as any).parentNode || (node as any).host || null;
        if (parent === node) break;
        node = parent;
      }
    } catch { /* ignore */ }
    return false;
  }

  function finishAllAnimations() {
    try {
      const animations = document.getAnimations?.();
      if (!animations) return;
      for (const anim of animations) {
        if (isWidgetAnimation(anim)) continue;
        try {
          anim.finish();
        } catch {
          try { anim.cancel(); } catch { /* ignore */ }
        }
      }
    } catch { /* getAnimations not supported */ }
  }

  // =========================================================================
  // Layer 3: JS animation library detection + inline style freeze
  // =========================================================================

  function patchGSAP() {
    const g = (window as any).gsap;
    if (g?.globalTimeline) {
      try {
        g.globalTimeline.timeScale(0);
        (window as any)._a11yGsapPatched = true;
      } catch { /* ignore */ }
    }
  }

  function restoreGSAP() {
    const g = (window as any).gsap;
    if ((window as any)._a11yGsapPatched && g?.globalTimeline) {
      try {
        g.globalTimeline.timeScale(1);
        delete (window as any)._a11yGsapPatched;
      } catch { /* ignore */ }
    }
  }

  /**
   * Force hidden-by-animation elements visible.
   * Only touches INLINE styles — never sets CSS !important on opacity etc.
   * which would break modals and intentionally hidden elements.
   */
  function forceHiddenElementsVisible() {
    // Only process elements near the viewport to avoid layout chaos on
    // Framer/scroll-animated pages where dozens of off-screen elements
    // are hidden via opacity:0 / translateY as part of scroll reveals.
    const viewH = window.innerHeight;
    const margin = viewH * 1.5; // 1.5x viewport ahead

    const all = document.querySelectorAll<HTMLElement>('*');
    for (const el of all) {
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'NOSCRIPT' || el.tagName === 'META' || el.tagName === 'LINK') continue;
      if (el.closest('#accessify-root') || el.closest('accessify-widget')) continue;
      if (el.dataset.a11yOrigStyle) continue; // Already processed

      const computed = window.getComputedStyle(el);
      if (computed.display === 'none') continue; // Intentionally hidden

      // Skip elements far below the current scroll position —
      // they will be handled when the user scrolls to them (periodic re-scan).
      const rect = el.getBoundingClientRect();
      if (rect.top > viewH + margin) continue; // Too far below viewport
      // Also skip if entirely above viewport (already scrolled past)
      if (rect.bottom < -margin) continue;

      const inline = el.style;
      let needsFix = false;
      const originals: Record<string, string> = {};

      // Inline opacity 0 or near-0 (computed, not stylesheet)
      const opacityVal = parseFloat(computed.opacity);
      if (opacityVal < 0.1 && inline.opacity !== '') {
        originals.opacity = inline.opacity;
        el.style.opacity = '1';
        needsFix = true;
      } else if (opacityVal < 0.1 && el.hasAttribute('data-framer-appear-id')) {
        // Framer appear elements: opacity set via WAAPI or framework
        originals.opacity = inline.opacity || '';
        el.style.opacity = '1';
        needsFix = true;
      }

      // Inline transform with translate that moves element off-screen
      if (inline.transform && /translate[XY]\([^0]/.test(inline.transform)) {
        originals.transform = inline.transform;
        el.style.transform = 'none';
        needsFix = true;
      }

      // Inline visibility hidden
      if (inline.visibility === 'hidden') {
        originals.visibility = inline.visibility;
        el.style.visibility = 'visible';
        needsFix = true;
      }

      // Inline clip-path that hides element
      if (inline.clipPath && inline.clipPath !== 'none') {
        originals.clipPath = inline.clipPath;
        el.style.clipPath = 'none';
        needsFix = true;
      }

      if (needsFix) {
        el.dataset.a11yOrigStyle = JSON.stringify(originals);
      }
    }
  }

  function restoreHiddenElements() {
    document.querySelectorAll<HTMLElement>('[data-a11y-orig-style]').forEach(el => {
      try {
        const orig: Record<string, string> = JSON.parse(el.dataset.a11yOrigStyle || '{}');
        for (const [prop, val] of Object.entries(orig)) {
          if (val === '') {
            el.style.removeProperty(prop);
          } else {
            (el.style as any)[prop] = val;
          }
        }
      } catch { /* malformed JSON */ }
      delete el.dataset.a11yOrigStyle;
    });
  }

  /**
   * Freeze inline-animated styles so scroll-handlers can't update them.
   * Takes a snapshot of current inline transforms and enforces them on scroll.
   */
  function freezeScrollAnimations() {
    document.querySelectorAll<HTMLElement>('[style]').forEach(el => {
      if (el.closest('#accessify-root') || el.closest('accessify-widget')) return;
      if (el.dataset.a11yFrozenStyle) return;
      const s = el.style;
      if (s.transform || s.opacity) {
        el.dataset.a11yFrozenStyle = JSON.stringify({
          transform: s.transform || '',
          opacity: s.opacity || '',
        });
      }
    });

    if (!scrollHandler) {
      scrollHandler = () => {
        if (!enabled) return;
        document.querySelectorAll<HTMLElement>('[data-a11y-frozen-style]').forEach(el => {
          try {
            const frozen = JSON.parse(el.dataset.a11yFrozenStyle || '{}');
            if (frozen.transform && el.style.transform !== frozen.transform) {
              el.style.transform = frozen.transform;
            }
          } catch { /* ignore */ }
        });
      };
      window.addEventListener('scroll', scrollHandler, { passive: true, capture: true });
    }
  }

  function unfreezeScrollAnimations() {
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler, true);
      scrollHandler = null;
    }
    document.querySelectorAll<HTMLElement>('[data-a11y-frozen-style]').forEach(el => {
      delete el.dataset.a11yFrozenStyle;
    });
  }

  // =========================================================================
  // Layer 4: Media
  // =========================================================================

  function pauseAllVideos() {
    document.querySelectorAll<HTMLVideoElement>('video').forEach(v => {
      if (!v.paused) {
        v.dataset.a11yPaused = 'true';
        v.pause();
        pausedVideos.push(v);
      }
    });
  }

  function interceptVideoPlay() {
    if (originalVideoPlay) return;
    originalVideoPlay = HTMLVideoElement.prototype.play;
    HTMLVideoElement.prototype.play = function () {
      if (enabled) return Promise.resolve();
      return originalVideoPlay!.call(this);
    };
  }

  function restoreVideoPlay() {
    if (originalVideoPlay) {
      HTMLVideoElement.prototype.play = originalVideoPlay;
      originalVideoPlay = null;
    }
  }

  function resumeAllVideos() {
    restoreVideoPlay();
    document.querySelectorAll<HTMLVideoElement>('video[data-a11y-paused]').forEach(v => {
      try { v.play(); } catch { /* gone */ }
      delete v.dataset.a11yPaused;
    });
    pausedVideos = [];
  }

  function freezeGif(img: HTMLImageElement) {
    const src = (img.currentSrc || img.src || '').toLowerCase();
    if (!src.match(/\.gif(\?|$)/i)) return;
    if (gifOriginals.has(img)) return;

    const doFreeze = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (w === 0 || h === 0) return;
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0);
        gifOriginals.set(img, img.src);
        img.src = c.toDataURL('image/png');
      } catch { /* CORS */ }
    };

    if (img.complete && img.naturalWidth > 0) doFreeze();
    else img.addEventListener('load', doFreeze, { once: true });
  }

  function freezeAllGifs() {
    document.querySelectorAll<HTMLImageElement>('img').forEach(freezeGif);
  }

  function restoreAllGifs() {
    for (const [img, src] of gifOriginals) {
      try { img.src = src; } catch { /* gone */ }
    }
    gifOriginals.clear();
  }

  function pauseSVG() {
    document.querySelectorAll('svg').forEach(s => {
      try { (s as any).pauseAnimations?.(); } catch { /* ignore */ }
    });
  }

  function resumeSVG() {
    document.querySelectorAll('svg').forEach(s => {
      try { (s as any).unpauseAnimations?.(); } catch { /* ignore */ }
    });
  }

  function stopMarquees() {
    document.querySelectorAll('marquee').forEach(m => {
      try { (m as any).stop?.(); } catch { /* ignore */ }
    });
  }

  function startMarquees() {
    document.querySelectorAll('marquee').forEach(m => {
      try { (m as any).start?.(); } catch { /* ignore */ }
    });
  }

  // =========================================================================
  // MutationObserver — dynamic content
  // =========================================================================

  function setupObserver() {
    if (mutationObserver) return;
    mutationObserver = new MutationObserver(mutations => {
      if (!enabled) return;
      let hasNewContent = false;
      for (const m of mutations) {
        if (m.addedNodes.length > 0) { hasNewContent = true; break; }
      }
      if (hasNewContent) {
        finishAllAnimations();
        forceHiddenElementsVisible();
        pauseAllVideos();
        freezeAllGifs();
        pauseSVG();
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  function teardownObserver() {
    mutationObserver?.disconnect();
    mutationObserver = null;
  }

  // =========================================================================
  // Periodic re-scan — frameworks create new animations constantly
  // =========================================================================

  function startPeriodicScan() {
    if (scanInterval) return;
    scanInterval = setInterval(() => {
      if (!enabled) return;
      finishAllAnimations();
    }, 500);
  }

  function stopPeriodicScan() {
    if (scanInterval) {
      clearInterval(scanInterval);
      scanInterval = null;
    }
  }

  // =========================================================================
  // Lifecycle
  // =========================================================================

  function activate() {
    if (enabled) return;
    enabled = true;

    // Layer 0: Intercept Element.animate() — catches ALL new WAAPI animations instantly
    patchAnimate();

    // Layer 1: CSS
    injectStyles();

    // Layer 2: Kill all currently running WAAPI animations
    finishAllAnimations();

    // Layer 3: JS libs + inline style fixes
    patchGSAP();
    forceHiddenElementsVisible();
    freezeScrollAnimations();

    // Layer 4: Media
    pauseAllVideos();
    interceptVideoPlay();
    freezeAllGifs();
    pauseSVG();
    stopMarquees();

    // Observers
    setupObserver();
    startPeriodicScan();

    // Delayed re-scans for async rendering frameworks
    for (const delay of [50, 200, 500, 1000, 2000, 4000]) {
      setTimeout(() => {
        if (!enabled) return;
        finishAllAnimations();
        forceHiddenElementsVisible();
        freezeScrollAnimations();
        pauseAllVideos();
        freezeAllGifs();
      }, delay);
    }

    localStorage.setItem(STORAGE_KEY, 'true');
  }

  function deactivate() {
    enabled = false;

    stopPeriodicScan();
    teardownObserver();
    restoreAnimate();
    unfreezeScrollAnimations();
    restoreGSAP();
    restoreHiddenElements();
    startMarquees();
    resumeSVG();
    restoreAllGifs();
    resumeAllVideos();
    removeStyles();

    localStorage.removeItem(STORAGE_KEY);
  }

  return {
    id: 'animation-stop',
    name: () => 'Stop Animations',
    description: 'Pause all animations, transitions, and auto-playing videos (WCAG 2.3.1)',
    icon: 'animation-stop',
    category: 'visual',
    activate,
    deactivate,
    getState: (): FeatureState => ({ id: 'animation-stop', enabled }),
    setState: (state: { enabled: boolean }) => {
      if (state.enabled) activate();
      else deactivate();
    },
  };
}
